import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { ISnFeedQueryParameters, ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnTag, SnTags } from '@lib/api/entities';
import { SnTagsAPI } from '@lib/api/services';
import { SnDataListComponent } from 'libs/ui/data-list/src/lib/components';

@Component({
  selector: 'sn-tag-location-dialog',
  templateUrl: './tag-location-dialog.component.html',
  styleUrls: ['./tag-location-dialog.component.scss'],
})
export class TagLocationDialogComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<SnTag>;
  public readonly searchControl: UntypedFormControl = new UntypedFormControl();
  public readonly loadData = this._loadData.bind(this);
  public readonly feedData = this._feedData.bind(this);
  public selection = new SelectionModel<SnTag>(true, []);

  public readonly columns: string[] = ['select', 'name'];

  public _querySearch: string;
  public pageSize;
  public selected;

  private readonly _destroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly _dialogRef: MatDialogRef<TagLocationDialogComponent>,
    private readonly _tagsAPI: SnTagsAPI,
  ) {}

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe((v) => {
        this._querySearch = v;
        this._reloadData();
      });

    this.selected = '20';
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    this._querySearch = this.searchControl.value;
    this._reloadData();
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  public clear(): void {
    this.searchControl.reset(null);
    this.submitForm();
  }

  public loadAllLocations(): void {
    this.pageSize = 'all';
    this._reloadData();
  }

  public loadTenLocations(): void {
    this.pageSize = 10;
    this._reloadData();
  }

  public loadTwentyLocations(): void {
    this.pageSize = 20;
    this._reloadData();
  }

  public masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.list.dataSource.data.forEach((row) => this.selection.select(row));
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.list.dataSource.data.length;
    return numSelected === numRows;
  }

  public onSelected(tag: any): void {
    this._dialogRef.close(tag);
  }

  public call(): void {
    const tags = this.selection.selected.map((tag) => tag);
    this.onSelected(tags);
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _loadData(params?: ISnListQueryParameters): Observable<ISnListResponse<SnTag>> {
    params.search = this._querySearch;
    return this._tagsAPI
      .getTagList(this._prepareQueryParams(params))
      .pipe(takeUntil(this._destroy$));
  }

  private _feedData(params: ISnFeedQueryParameters): Observable<SnTags[]> {
    return this._tagsAPI.feedTags(this._prepareQueryParams(params));
  }

  private _prepareQueryParams<T extends ISnListQueryParameters>(params: any): any {
    return {
      ordering: {
        active: 'name',
        direction: 'asc',
      },
      search: this._querySearch,
      page: params.page,
      pageSize: this.pageSize ? this.pageSize : params.pageSize,
    };
  }
}
